import * as React from 'react';

import * as Sentry from '@sentry/react';

import type { LearnerObjectFieldId } from 'bundles/personalization-settings/constants';

/** Delay before a save fires after the last edit, so rapid edits collapse into one call. */
export const SETTING_SAVE_DEBOUNCE_MS = 500;

/** How long the success notification stays visible before auto-clearing. */
export const SETTING_SAVE_SUCCESS_DISPLAY_MS = 3000;

export type SettingSaveResult = {
  success: boolean;
  errors: string[];
};

export type UseSettingSaveResult<T> = {
  /** The value to render: the learner's pending edit, or the last known-good value. */
  value: T;
  /** Set once a save resolves; `undefined` when there's nothing to show. */
  notificationType?: 'success' | 'error';
  /** Call with the new value on every edit; debounces the underlying save. */
  onChange: (value: T) => void;
};

/**
 * Wraps a setting row's save flow: applies edits optimistically, debounces the
 * network call so rapid edits collapse into one save, and reports success/error.
 * On error the value reverts to the last successfully-saved value -- a failed
 * save never leaves the row showing an unsaved edit. On success the override
 * is cleared (not just left pointing at the saved value), so a later external
 * change to `seededValue` -- e.g. a different component patching the same
 * shared context field -- is reflected live instead of staying hidden behind
 * this edit until a full reload. Callers are expected to patch their own seed
 * to the saved value before `save` resolves, so this doesn't cause a flicker.
 *
 * @param fieldName - Identifies the setting in Sentry reports on save failure.
 */
const useSettingSave = <T>(
  seededValue: T,
  save: (value: T) => Promise<SettingSaveResult>,
  fieldName: LearnerObjectFieldId
): UseSettingSaveResult<T> => {
  const [override, setOverride] = React.useState<T | undefined>(undefined);
  const [notificationType, setNotificationType] = React.useState<'success' | 'error' | undefined>(undefined);

  const lastGoodValueRef = React.useRef(seededValue);
  const saveRef = React.useRef(save);
  saveRef.current = save;
  const latestCallIdRef = React.useRef(0);

  if (override === undefined) {
    lastGoodValueRef.current = seededValue;
  }

  const debounceTimeoutRef = React.useRef<ReturnType<typeof setTimeout>>();
  const successTimeoutRef = React.useRef<ReturnType<typeof setTimeout>>();

  const clearTimeouts = React.useCallback<() => void>(() => {
    clearTimeout(debounceTimeoutRef.current);
    clearTimeout(successTimeoutRef.current);
  }, []);

  React.useEffect(() => clearTimeouts, [clearTimeouts]);

  const onChange = React.useCallback<(value: T) => void>(
    (value) => {
      setOverride(value);
      setNotificationType(undefined);
      clearTimeouts();

      latestCallIdRef.current += 1;
      const callId = latestCallIdRef.current;

      debounceTimeoutRef.current = setTimeout(async () => {
        let success = false;
        let errors: string[] = [];
        let threw = false;
        try {
          ({ success, errors } = await saveRef.current(value));
        } catch (error) {
          success = false;
          threw = true;
          Sentry.captureException(error, {
            extra: { message: `Failed to save personalization setting: ${fieldName}` },
          });
        }

        if (callId !== latestCallIdRef.current) {
          return;
        }

        if (!success && !threw) {
          Sentry.captureMessage(`Failed to save personalization setting: ${fieldName}`, { extra: { errors } });
        }

        if (success) {
          lastGoodValueRef.current = value;
          // Clear the override once it's confirmed saved -- by now `seededValue`
          // reflects this same value (the caller's `save` patches shared state
          // before resolving), so this doesn't change what's on screen. Leaving
          // override set would permanently pin `value` to this one edit, hiding
          // any later external update to the same field (e.g. another writer
          // patching shared context) until a full page reload re-seeds it.
          setOverride(undefined);
          setNotificationType('success');
          successTimeoutRef.current = setTimeout(() => setNotificationType(undefined), SETTING_SAVE_SUCCESS_DISPLAY_MS);
        } else {
          setOverride(lastGoodValueRef.current);
          setNotificationType('error');
        }
      }, SETTING_SAVE_DEBOUNCE_MS);
    },
    [clearTimeouts, fieldName]
  );

  return {
    value: override !== undefined ? override : seededValue,
    notificationType,
    onChange,
  };
};

export default useSettingSave;
